"use client";

import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, X } from "lucide-react";
import { Card, CardBody } from "@/components/ui/Card";
import { InstallAppSheet } from "@/components/install/InstallAppSheet";
import { useChores } from "@/lib/hooks/useChores";
import { useCompletions } from "@/lib/hooks/useCompletions";
import { useHouseholdTransactions } from "@/lib/hooks/useHouseholdTransactions";
import { useMembers } from "@/lib/hooks/useMembers";
import { isStandalone } from "@/lib/utils/pwa";

const DISMISS_KEY = "kd_first_week_checklist_dismissed";

type Props = {
  hid: string;
  /** Otwiera AddChildSheet w ParentDashboard. */
  onAddChild: () => void;
};

type Step = {
  key: string;
  label: string;
  sublabel?: string;
  done: boolean;
  /** Route do nawigacji — albo `onClick` niżej. */
  to?: string;
  onClick?: () => void;
};

/**
 * Checklist „Pierwszy tydzień z Sianko" — prowadzi rodzica przez golden
 * path (dziecko → zadania → approve → wypłata → instalacja) zamiast
 * zostawiać go z 18 ekranami do samodzielnego odkrycia.
 *
 * Stany liczone WYŁĄCZNIE z istniejących danych (members, chores,
 * approved completions, transactions) — zero nowych kolekcji. Znika sam,
 * gdy wszystko odhaczone; X chowa na stałe (localStorage). Bez presji:
 * to mapa, nie zadanie domowe.
 */
export function FirstWeekChecklist({ hid, onAddChild }: Props) {
  const { members } = useMembers(hid);
  const { chores } = useChores(hid);
  const { completions: approved } = useCompletions(hid, {
    status: "approved",
  });
  const { transactions } = useHouseholdTransactions(hid);
  const [installOpen, setInstallOpen] = useState(false);
  const [dismissed, setDismissed] = useState(
    () =>
      typeof window !== "undefined" &&
      localStorage.getItem(DISMISS_KEY) === "1",
  );

  if (dismissed) return null;

  const childCount = members.filter((m) => m.role === "child").length;
  const activeChores = chores.filter((c) => c.active).length;
  const hasApproved = approved.length > 0;
  const hasPayout = transactions.some((t) => t.type === "payout");

  const steps: Step[] = [
    {
      key: "child",
      label: "Dodaj dziecko",
      done: childCount > 0,
      onClick: onAddChild,
    },
    {
      key: "chores",
      label: "Dodaj 3 zadania",
      sublabel: activeChores < 3 ? `${activeChores}/3` : undefined,
      done: activeChores >= 3,
      to: "/chores/new",
    },
    {
      key: "approve",
      label: "Zatwierdź pierwsze zgłoszenie",
      sublabel: hasApproved ? undefined : "dziecko najpierw zgłasza zadanie",
      done: hasApproved,
      to: "/approvals",
    },
    {
      key: "payout",
      label: "Zrób pierwszą wypłatę",
      done: hasPayout,
      to: "/wallet",
    },
    {
      key: "install",
      label: "Zainstaluj Sianko na telefonie",
      sublabel: "ikona + powiadomienia o zgłoszeniach",
      done: isStandalone(),
      onClick: () => setInstallOpen(true),
    },
  ];

  const doneCount = steps.filter((s) => s.done).length;
  if (doneCount === steps.length) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // prywatny tryb — chowamy tylko do końca sesji
    }
    setDismissed(true);
  };

  return (
    <Card>
      <CardBody className="pt-4 space-y-2.5">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[11px] font-bold uppercase tracking-[0.05em] text-muted">
            🌱 Pierwszy tydzień z Sianko · {doneCount}/{steps.length}
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Ukryj checklistę"
            className="text-muted hover:text-ink p-1 -m-1"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <ul className="space-y-1">
          {steps.map((s) => (
            <li key={s.key}>
              <StepRow step={s} />
            </li>
          ))}
        </ul>
      </CardBody>

      <InstallAppSheet
        open={installOpen}
        onClose={() => setInstallOpen(false)}
      />
    </Card>
  );
}

function StepRow({ step }: { step: Step }) {
  const inner = (
    <>
      <span
        className={`h-5 w-5 rounded-full flex items-center justify-center flex-shrink-0 ${
          step.done
            ? "bg-green text-white"
            : "border-[1.5px] border-line bg-card"
        }`}
      >
        {step.done && <Check className="h-3 w-3" />}
      </span>
      <span className="min-w-0 flex-1">
        <span
          className={`block text-sm ${
            step.done ? "text-muted line-through" : "font-medium"
          }`}
        >
          {step.label}
        </span>
        {!step.done && step.sublabel && (
          <span className="block text-[11px] text-muted">{step.sublabel}</span>
        )}
      </span>
    </>
  );

  const rowCls = "flex items-center gap-2.5 rounded-[10px] px-1.5 py-1.5";

  if (step.done) return <div className={rowCls}>{inner}</div>;
  if (step.to) {
    return (
      <Link to={step.to} className={`${rowCls} hover:bg-soft`}>
        {inner}
      </Link>
    );
  }
  return (
    <button
      type="button"
      onClick={step.onClick}
      className={`${rowCls} w-full text-left hover:bg-soft`}
    >
      {inner}
    </button>
  );
}
